import React, { useEffect, useState } from 'react';
import { Container } from '../../components/styleComponents';
import Loader from '../../components/Loader';
import { ILotacao } from 'src/interfaces';
import api from 'src/service/api';
import { toast } from 'react-toastify';

import {
    Toolbar, TotalRegistros, FormContainer,
    FormRow, FormLabel, FormInput, CheckboxContainer,
    TableContainer, Table, TableRow, Pagination,
    SearchContainer,
} from './style';
import {
    FaAngleDoubleLeft, FaAngleLeft, FaAngleRight,
    FaAngleDoubleRight, FaSave, FaTrash, FaEraser,
} from 'react-icons/fa';

const Lotacoes: React.FC = () => {
    const [lotacoes, setLotacoes] = useState<ILotacao[]>([]);
    const [loading, setLoading] = useState(true);

    const [page, setPage] = useState(1);
    const pageSize = 10;

    const [lotacaoSelecionada, setLotacaoSelecionada] = useState<ILotacao | null>(null);

    const [chaveOriginal, setChaveOriginal] = useState<{
        usuMatricula: number;
        setId: number;
        lotInicio: string;
    } | null>(null);

    const [form, setForm] = useState<Partial<ILotacao>>({});

    const modoInsercao = lotacaoSelecionada === null;

    const getChaveLotacao = (
        lotacao: Partial<ILotacao>,
    ) =>
        `${lotacao.usuMatricula}_${lotacao.setId}_${lotacao.lotInicio}`;

    const [filtroMatricula, setFiltroMatricula] = useState('');

    const matriculasFiltradas = lotacoes.filter((lotacao) => lotacao.usuMatricula.toString()
        ?.includes(filtroMatricula.toString()),
);

    const updateForm = (
        field: keyof ILotacao,
        value: string | number | boolean,
    ) => {
        setForm((current) => ({
            ...current,
            [field]: value,
        }));
    };

    const carregarLotacoes = async () => {
        try {
            setLoading(true);
            const token = sessionStorage.getItem('token');
            api.defaults.headers.Authorization = `Bearer ${token}`;
            api.defaults.headers.chaValor = process.env.REACT_APP_CHAVE_API;
            const response = await api.get('/lotacoes');
            setLotacoes(response.data);
        } catch (error) {
            console.error(
                'Erro ao carregar lotações',
                error,
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        carregarLotacoes();
    }, []);

    const limparFormulario = () => {
        setLotacaoSelecionada(null);
        setChaveOriginal(null);
        setForm({});
        setFiltroMatricula('');
        setPage(1);
    };

    const selecionarLotacao = (
        lotacao: ILotacao,
    ) => {
        setLotacaoSelecionada(lotacao);

        setChaveOriginal({
            usuMatricula: lotacao.usuMatricula,
            setId: lotacao.setId,
            lotInicio: lotacao.lotInicio,
        });

        setForm({
            usuMatricula: lotacao.usuMatricula,
            setId: lotacao.setId,
            lotInicio: formatarDataParaInput(
                lotacao.lotInicio,
            ),
            lotManual: lotacao.lotManual,
        });
    };

    const salvar = async () => {
        if (!validarFormulario()) {
            return;
        }
        try {
            const token = sessionStorage.getItem('token');
            api.defaults.headers.Authorization = `Bearer ${token}`;
            api.defaults.headers.chaValor = process.env.REACT_APP_CHAVE_API;
            const payload = {
                usuMatricula: form.usuMatricula,
                setId: form.setId,
                lotInicio: formatarDataParaApi(
                    form.lotInicio as string,
                ),
                lotManual: form.lotManual,
            };
            if (modoInsercao) {
                await api.post(
                    '/lotacoes/inclui',
                    payload,
                );
            } else {
                await api.put('/lotacoes/atualiza', {
                    chaveOriginal,
                    dadosNovos: payload,
                });
            }
            await carregarLotacoes();
            limparFormulario();
            toast.success('Lotação salva com sucesso.');
        } catch (error) {
            toast.error('Erro ao salvar lotação.');
            console.error('Erro ao salvar lotação', error);
        }
    };

    const excluir = async () => {
        if (!lotacaoSelecionada) {
            return;
        }
        const confirmou = window.confirm(
            `Deseja excluir a lotação da matrícula ${lotacaoSelecionada.usuMatricula}?`,
        );

        if (!confirmou) {
            return;
        }
        try {
            const token = sessionStorage.getItem('token');
            api.defaults.headers.Authorization = `Bearer ${token}`;
            api.defaults.headers.chaValor = process.env.REACT_APP_CHAVE_API;
            await api.delete('/lotacoes/exclui', {
                data: {
                    usuMatricula:
                        lotacaoSelecionada.usuMatricula,
                    setId:
                        lotacaoSelecionada.setId,
                    lotInicio:
                        lotacaoSelecionada.lotInicio,
                },
            });
            await carregarLotacoes();
            limparFormulario();
            setPage(1);
            toast.success('Lotação excluída com sucesso.');
        } catch (error) {
            toast.error('Erro ao excluir lotação.');
            console.error(
                'Erro ao excluir lotação',
                error,
            );
        }
    };

    const totalPages = Math.ceil(
        matriculasFiltradas.length / pageSize,
    );

    const lotacoesPaginadas = matriculasFiltradas.slice(
        (page - 1) * pageSize,
        page * pageSize,
    );

    if (loading) {
        return <Loader />;
    }

    const formatarDataParaInput = (
        data?: string | null,
    ): string => {
        if (!data) return '';
        return data.substring(0, 10);
    };

    const formatarDataParaApi = (
        data?: string,
    ): string | null => {
        if (!data) return null;
        return `${data} 00:00:00.000`;
    };

    const somenteNumeros = (valor: string): string => {
        return valor.replace(/\D/g, '');
    };

    const validarFormulario = (): boolean => {
        const campos: string[] = [];
        if (!form.setId) campos.push('Set_id');
        if (!form.usuMatricula) campos.push('Matricula');
        if (!form.lotInicio) campos.push('Inicio');
        if (campos.length > 0) {
            alert(`Os seguintes campos são obrigatórios:\n\n${campos.join('\n')}`,);
            return false;
        }
        return true;
    };
    return (
        <Container>
            <h2>Lotações</h2>
            <Toolbar>
                <button type="button" onClick={salvar} >
                    <FaSave />
                    {modoInsercao ? 'Incluir' : 'Atualizar'}
                </button>

                <button type="button" onClick={limparFormulario}><FaEraser />Limpar campos</button>

                <button type="button" disabled={!lotacaoSelecionada} onClick={excluir}><FaTrash />Excluir</button>

                <TotalRegistros>
                    {modoInsercao
                        ? 'Modo Inclusão'
                        : `Modo Edição - Matrícula ${form.usuMatricula} / Setor ${form.setId}`}
                    - Total de registros:
                    {lotacoes.length}
                </TotalRegistros>
            </Toolbar>

            <FormContainer>
                <FormRow>
                    <FormLabel htmlFor="setId">Set_id</FormLabel>
                    <FormInput
                        id="setId" width="80px" type="text"
                        value={form.setId ?? ''}
                        onChange={(e) => {
                            const valor = somenteNumeros(e.target.value);
                            updateForm(
                                'setId',
                                valor === '' ? '' : Number(valor),
                            );
                        }}
                    />
                    <FormLabel htmlFor="setId">Matricula</FormLabel>
                    <FormInput
                        id="usuMatricula" width="80px"
                        type="text"
                        value={form.usuMatricula ?? ''}
                        onChange={(e) => {
                            const valor = somenteNumeros(e.target.value);
                            updateForm(
                                'usuMatricula',
                                valor === '' ? '' : Number(valor),
                            );
                        }}
                    />
                    <FormLabel
                        htmlFor="lotInicio"
                        style={{
                            width: '140px', minWidth: '140px',
                        }}
                    >Inicio
                    </FormLabel>
                    <FormInput
                        id="lotInicio"
                        disabled={!modoInsercao}
                        width="170px"
                        type="date"
                        value={
                            typeof form.lotInicio === 'string'
                                ? form.lotInicio
                                : ''
                        }
                        onChange={(e) =>
                            updateForm(
                                'lotInicio',
                                e.target.value,
                            )
                        }
                    />
                    <FormLabel
                        style={{
                            width: '60px',
                            minWidth: '60px',
                            marginLeft: '5px',
                        }}
                    >Manual
                    </FormLabel>
                    <CheckboxContainer>
                        <input
                            type="checkbox"
                            checked={form.lotManual ?? false}
                            onChange={(e) =>
                                updateForm(
                                    'lotManual',
                                    e.target.checked,
                                )
                            }
                        />
                    </CheckboxContainer>
                </FormRow>
            </FormContainer>
            <SearchContainer>
                <label htmlFor="pesquisaMatricula">Pesquisar matricula</label>
                <input
                    id="pesquisaMatricula"
                    type="text"
                    value={filtroMatricula}
                    onChange={(e) => {
                        setFiltroMatricula(e.target.value);
                        setPage(1);
                    }}
                />
                <span>Encontrados: {matriculasFiltradas.length}</span>
            </SearchContainer>
            <TableContainer>
                <Table>
                    <thead>
                        <tr>
                            <th>Set_id</th>
                            <th>Matricula</th>
                            <th>Inicio</th>
                            <th>Manual</th>
                        </tr>
                    </thead>
                    <tbody>
                        {lotacoesPaginadas.map(
                            (lotacao) => (
                                <TableRow
                                    key={getChaveLotacao(lotacao)}
                                    selected={
                                        !!lotacaoSelecionada &&
                                        getChaveLotacao(
                                            lotacaoSelecionada,
                                        ) === getChaveLotacao(lotacao)
                                    }
                                    onClick={() =>
                                        selecionarLotacao(lotacao,)
                                    }
                                >
                                    <td>
                                        { lotacao.setId }
                                    </td>
                                    <td>
                                        { lotacao.usuMatricula }
                                    </td>
                                    <td>
                                        { formatarDataParaInput(lotacao.lotInicio) }
                                    </td>
                                    <td>
                                        {lotacao.lotManual ? 'Sim' : 'Não'}
                                    </td>
                                </TableRow>
                            ),
                        )}
                    </tbody>
                </Table>
            </TableContainer>
            <Pagination>
                <button type="button" disabled={page === 1} onClick={() => setPage(1)}><FaAngleDoubleLeft />Primeira</button>
                <button
                    type="button"
                    disabled={page === 1}
                    onClick={() =>
                        setPage((current) => current - 1)
                    }
                >
                    <FaAngleLeft />
                    Anterior
                </button>
                <span>Página {page} de {totalPages || 1}</span>

                <button
                    type="button"
                    disabled={
                        page >= totalPages ||
                        totalPages === 0
                    }
                    onClick={() =>
                        setPage((current) => current + 1)
                    }
                >
                    <FaAngleRight />
                    Próxima
                </button>
                <button
                    type="button"
                    disabled={
                        page >= totalPages ||
                        totalPages === 0
                    }
                    onClick={() => setPage(totalPages)}
                >
                    <FaAngleDoubleRight />
                    Última
                </button>
            </Pagination>
        </Container>
    );
};
export default Lotacoes;
